import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Plot from 'react-plotly.js';
import { CenecLoadingBlock } from './CenecSpinner';
import BatterySimulationView from './BatterySimulationView';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const RANGES = [
  { key: '1h',  label: '1 hour'  },
  { key: '6h',  label: '6 hours' },
  { key: '24h', label: '24 hours'},
  { key: '7d',  label: '7 days'  },
  { key: '30d', label: '30 days' },
];

// Match the UFS GRRG dashboard thresholds — nominal 50 Hz, ±0.2 Hz band.
const NOMINAL_HZ = 50.0;
const UPPER_HZ = 50.2;
const LOWER_HZ = 49.8;

// UFS gauge "steps" palette:
//   < 49.8        red
//   49.8 → 50.0   yellow
//   50.0 → 50.2   orange
//   > 50.2        red
const COLOR_RED    = '#E02F44';
const COLOR_YELLOW = '#FADE2A';
const COLOR_ORANGE = '#FF780A';
const COLOR_GREEN  = '#73BF69';

// Grafana-style panel chrome.
const PANEL_BG = '#181b1f';
const PANEL_BG_PLOT = '#111217';
const PANEL_BORDER = 'rgba(255,255,255,0.07)';
const PANEL_GRID = 'rgba(255,255,255,0.08)';
const PANEL_TEXT = '#d8d9da';
const PANEL_TEXT_DIM = '#9da0a4';

function colorForFrequency(hz) {
  if (hz === null || hz === undefined || Number.isNaN(hz)) return COLOR_GREEN;
  if (hz < LOWER_HZ || hz > UPPER_HZ) return COLOR_RED;
  if (hz < NOMINAL_HZ) return COLOR_YELLOW;
  if (hz > NOMINAL_HZ) return COLOR_ORANGE;
  return COLOR_GREEN;
}

// UFS mapping: value text is green if 49 ≤ value ≤ 51.
function gaugeValueColor(hz) {
  if (hz === null || hz === undefined || Number.isNaN(hz)) return PANEL_TEXT;
  return (hz >= 49 && hz <= 51) ? COLOR_GREEN : COLOR_RED;
}

const plotConfig = { responsive: true, displayModeBar: false };

function formatTimestamp(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { hour12: false });
}

// Rangeselector buttons tuned to the currently-loaded window.
function rangeButtonsForRange(rangeKey) {
  switch (rangeKey) {
    case '1h':  return [
      { count: 15, label: '15m', step: 'minute', stepmode: 'backward' },
      { count: 30, label: '30m', step: 'minute', stepmode: 'backward' },
      { step: 'all', label: '1h' },
    ];
    case '6h':  return [
      { count: 1, label: '1h', step: 'hour',  stepmode: 'backward' },
      { count: 3, label: '3h', step: 'hour',  stepmode: 'backward' },
      { step: 'all', label: '6h' },
    ];
    case '24h': return [
      { count: 1,  label: '1h',  step: 'hour', stepmode: 'backward' },
      { count: 6,  label: '6h',  step: 'hour', stepmode: 'backward' },
      { count: 12, label: '12h', step: 'hour', stepmode: 'backward' },
      { step: 'all', label: '24h' },
    ];
    case '7d':  return [
      { count: 6,  label: '6h', step: 'hour', stepmode: 'backward' },
      { count: 1,  label: '1d', step: 'day',  stepmode: 'backward' },
      { count: 3,  label: '3d', step: 'day',  stepmode: 'backward' },
      { step: 'all', label: '7d' },
    ];
    case '30d': return [
      { count: 1, label: '1d',  step: 'day', stepmode: 'backward' },
      { count: 7, label: '7d',  step: 'day', stepmode: 'backward' },
      { step: 'all', label: '30d' },
    ];
    default:    return [{ step: 'all', label: 'All' }];
  }
}

// Open a Plotly chart in a standalone tab at full screen.
function openChartFullscreen(data, layout, title) {
  const fsLayout = JSON.parse(JSON.stringify(layout));
  fsLayout.paper_bgcolor = PANEL_BG;
  fsLayout.plot_bgcolor  = PANEL_BG_PLOT;
  fsLayout.height = undefined;
  fsLayout.autosize = true;
  if (fsLayout.margin) fsLayout.margin = { t: 60, r: 40, b: 80, l: 70 };
  if (fsLayout.title && typeof fsLayout.title === 'object') {
    fsLayout.title.font = { ...fsLayout.title.font, size: 18 };
  }
  const html = `<!DOCTYPE html>
<html><head>
<title>${title || 'NRG Frequency'}</title>
<script src="https://cdn.plot.ly/plotly-2.35.0.min.js"></script>
<style>
  body { margin:0; background:${PANEL_BG}; overflow:hidden; color:${PANEL_TEXT};
         font-family: Inter, "Work Sans", sans-serif; }
  #chart { width:100vw; height:100vh; }
</style>
</head><body>
<div id="chart"></div>
<script>
  var data = ${JSON.stringify(data)};
  var layout = ${JSON.stringify(fsLayout)};
  Plotly.newPlot('chart', data, layout, {responsive:true, displayModeBar:true});
  window.addEventListener('resize', function() { Plotly.Plots.resize('chart'); });
</script>
</body></html>`;
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

const ExpandIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

function computeStats(values) {
  if (!values || values.length === 0) return null;
  let min = Infinity, max = -Infinity, sum = 0;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
  }
  const mean = sum / values.length;
  let sqDiff = 0;
  for (const v of values) sqDiff += (v - mean) ** 2;
  const variance = sqDiff / values.length;
  return { min, max, mean, variance, last: values[values.length - 1] };
}

/* Grafana-style panel chrome around a chart, with optional expand button.
   Content area sizes to its children — no trailing dead space. */
const Panel = ({ title, children, flex, onExpand }) => (
  <div style={{
    background: PANEL_BG,
    border: `1px solid ${PANEL_BORDER}`,
    borderRadius: 3,
    overflow: 'hidden',
    flex: flex || '1 1 auto',
    minWidth: 240,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  }}>
    <div style={{
      padding: '8px 12px',
      fontSize: 12,
      fontWeight: 500,
      color: PANEL_TEXT,
      borderBottom: `1px solid ${PANEL_BORDER}`,
      letterSpacing: 0.2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      <span>{title}</span>
      {onExpand && (
        <button
          onClick={onExpand}
          title="Open in full screen"
          style={{
            background: 'transparent', border: 'none', color: PANEL_TEXT_DIM,
            cursor: 'pointer', padding: '2px 4px', display: 'flex', alignItems: 'center',
          }}
          onMouseEnter={e => (e.currentTarget.style.color = PANEL_TEXT)}
          onMouseLeave={e => (e.currentTarget.style.color = PANEL_TEXT_DIM)}
        >
          <ExpandIcon />
        </button>
      )}
    </div>
    {children}
  </div>
);

const FrequencyDataView = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [range, setRange] = useState('1h');
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSites = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/grid-frequency/sites`);
      if (!r.ok) return;
      const json = await r.json();
      setSites(json.sites || []);
      // Functional form so the guard reads the CURRENT selection rather than the
      // one captured when this request started. fetchSites also runs on a 10 s
      // poll, so a request is regularly in flight; with a captured selectedSite,
      // a click landing mid-request was overwritten by the default site as soon
      // as that request resolved. Empty deps keep the poll interval stable too.
      if (json.sites && json.sites.length > 0) {
        setSelectedSite(prev => prev || json.sites[0].site);
      }
    } catch (e) { /* backend not running */ }
  }, []);

  const fetchData = useCallback(async () => {
    if (!selectedSite || selectedSite === 'battery') return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/grid-frequency/data?site=${selectedSite}&range=${range}`);
      if (r.ok) setData(await r.json());
    } catch (e) { console.error('Failed to fetch data:', e); }
    finally { setLoading(false); }
  }, [selectedSite, range]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/grid-frequency/stats`);
      if (r.ok) setStats(await r.json());
    } catch (e) { /* backend not running */ }
  }, []);

  useEffect(() => {
    fetchSites();
    fetchStats();
    const id = setInterval(() => { fetchSites(); fetchStats(); }, 10000);
    return () => clearInterval(id);
  }, [fetchSites, fetchStats]);

  useEffect(() => {
    fetchData();
    const fast = (range === '1h' || range === '6h');
    if (!fast) return;
    const id = setInterval(fetchData, 5000);
    return () => clearInterval(id);
  }, [fetchData, range]);

  const freq = data?.series?.frequency_hz;
  const selectedSiteRecord = sites.find(s => s.site === selectedSite);

  const calcs = useMemo(() => computeStats(freq?.value), [freq]);

  // ------- Frequency chart traces -------
  const freqTraces = [];
  if (freq && freq.ts.length > 0) {
    const firstTs = freq.ts[0];
    const lastTs = freq.ts[freq.ts.length - 1];

    // Three reference lines drawn BEHIND the data line (UFS uses dashed
    // threshold lines via `thresholdsStyle.mode: "dashed"`).
    freqTraces.push({
      x: [firstTs, lastTs], y: [UPPER_HZ, UPPER_HZ],
      mode: 'lines', name: `Upper ${UPPER_HZ.toFixed(1)} Hz`,
      line: { color: COLOR_RED, width: 1, dash: 'dash' },
      hoverinfo: 'skip', showlegend: false,
    });
    freqTraces.push({
      x: [firstTs, lastTs], y: [NOMINAL_HZ, NOMINAL_HZ],
      mode: 'lines', name: `Nominal ${NOMINAL_HZ.toFixed(1)} Hz`,
      line: { color: COLOR_GREEN, width: 1, dash: 'dash' },
      hoverinfo: 'skip', showlegend: false,
    });
    freqTraces.push({
      x: [firstTs, lastTs], y: [LOWER_HZ, LOWER_HZ],
      mode: 'lines', name: `Lower ${LOWER_HZ.toFixed(1)} Hz`,
      line: { color: COLOR_RED, width: 1, dash: 'dash' },
      hoverinfo: 'skip', showlegend: false,
    });

    // Data line — single color picked from the threshold palette based on the
    // latest value (matches UFS `seriesBy: "last"` continuous color mode).
    const lastValue = freq.value[freq.value.length - 1];
    freqTraces.push({
      x: freq.ts, y: freq.value,
      mode: 'lines', name: 'Frequency',
      line: { color: colorForFrequency(lastValue), width: 1 },
      hovertemplate: '%{x|%H:%M:%S}<br>%{y:.3f} Hz<extra></extra>',
      showlegend: false,
    });
  }

  // Y-axis range: fixed 49.4–50.4 Hz floor; expand further if data is wilder.
  let yMin = 49.4, yMax = 50.4;
  if (calcs) {
    yMin = Math.min(yMin, calcs.min - 0.01);
    yMax = Math.max(yMax, calcs.max + 0.01);
  }

  const freqLayout = {
    paper_bgcolor: PANEL_BG,
    plot_bgcolor: PANEL_BG_PLOT,
    font: { color: PANEL_TEXT, family: 'Inter, "Work Sans", sans-serif', size: 11 },
    margin: { t: 40, r: 16, b: 36, l: 50 },
    height: 620,
    showlegend: false,
    // Preserve user pan/zoom across the 5 s auto-refresh ticks.
    uirevision: range,
    xaxis: {
      type: 'date',
      gridcolor: PANEL_GRID, linecolor: PANEL_BORDER,
      tickfont: { color: PANEL_TEXT_DIM, size: 10 },
      showspikes: true, spikecolor: PANEL_TEXT_DIM, spikethickness: 1, spikedash: 'dot', spikemode: 'across',
      rangeslider: {
        visible: true,
        bgcolor: PANEL_BG_PLOT,
        bordercolor: PANEL_BORDER,
        borderwidth: 1,
        thickness: 0.08,
      },
      rangeselector: {
        bgcolor: 'rgba(0,0,0,0.4)',
        activecolor: 'rgba(78,204,163,0.3)',
        bordercolor: PANEL_BORDER,
        borderwidth: 1,
        font: { color: PANEL_TEXT, size: 10 },
        x: 0, y: 1.12,
        buttons: rangeButtonsForRange(range),
      },
    },
    yaxis: {
      gridcolor: PANEL_GRID, linecolor: PANEL_BORDER, zeroline: false,
      range: [yMin, yMax],
      tickformat: '.3f',
      ticksuffix: ' Hz',
      tickfont: { color: PANEL_TEXT_DIM, size: 10 },
      fixedrange: false,
    },
    hovermode: 'x unified',
    hoverlabel: { bgcolor: PANEL_BG, bordercolor: PANEL_BORDER, font: { color: PANEL_TEXT } },
  };

  // ------- Gauge (matches UFS panel 7) -------
  const latestFreq = freq && freq.value.length > 0 ? freq.value[freq.value.length - 1] : null;
  const gaugeTrace = latestFreq !== null ? [{
    type: 'indicator',
    mode: 'gauge+number',
    value: latestFreq,
    number: {
      valueformat: '.3f',
      suffix: ' Hz',
      font: { size: 36, color: gaugeValueColor(latestFreq), family: 'Inter, "Work Sans", sans-serif' },
    },
    gauge: {
      shape: 'angular',
      axis: {
        range: [49.7, 50.3],
        tickvals: [49.7, LOWER_HZ, NOMINAL_HZ, UPPER_HZ, 50.3],
        ticktext: ['49.7', '49.8', '50.0', '50.2', '50.3'],
        tickcolor: PANEL_TEXT_DIM,
        tickfont: { color: PANEL_TEXT_DIM, size: 10 },
        tickwidth: 1,
      },
      bar: { color: 'rgba(255,255,255,0.92)', thickness: 0.22 },
      bgcolor: PANEL_BG_PLOT,
      borderwidth: 0,
      steps: [
        { range: [49.7, LOWER_HZ],         color: COLOR_RED    },
        { range: [LOWER_HZ, NOMINAL_HZ],   color: COLOR_YELLOW },
        { range: [NOMINAL_HZ, UPPER_HZ],   color: COLOR_ORANGE },
        { range: [UPPER_HZ, 50.3],         color: COLOR_RED    },
      ],
      threshold: {
        line: { color: COLOR_GREEN, width: 4 },
        thickness: 0.92,
        value: NOMINAL_HZ,
      },
    },
  }] : [];

  const gaugeLayout = {
    paper_bgcolor: PANEL_BG,
    plot_bgcolor: PANEL_BG,
    font: { color: PANEL_TEXT, family: 'Inter, "Work Sans", sans-serif' },
    margin: { t: 28, r: 24, b: 24, l: 24 },
    height: 360,
  };

  return (
    <div className="climate-view">
      {/* ===== Left panel: site list ===== */}
      <div className="climate-left">
        <div className="climate-left-header">
          <h3>Sites</h3>
        </div>
        <div className="location-list">
          {sites.map(s => (
            <div
              key={s.site}
              className={`location-item ${selectedSite === s.site ? 'active' : ''}`}
              onClick={() => setSelectedSite(s.site)}
            >
              <div className="location-item-header">
                <span className="location-name">{s.label}</span>
              </div>
              <div className="location-meta">
                {s.row_count?.toLocaleString()} samples
                {s.last_value !== null && ` · ${s.last_value.toFixed(3)} Hz`}
              </div>
              <div className="location-meta" style={{ fontSize: 11, opacity: 0.7 }}>
                Updated: {formatTimestamp(s.last_ts)}
              </div>
            </div>
          ))}
          <div
            className={`location-item ${selectedSite === 'battery' ? 'active' : ''}`}
            onClick={() => setSelectedSite('battery')}
          >
            <div className="location-item-header">
              <span className="location-name">Battery</span>
            </div>
            <div className="location-meta" style={{ fontSize: 11, opacity: 0.7 }}>
              BESS frequency-regulation model
            </div>
          </div>
          {sites.length === 0 && (
            <div className="location-empty">No frequency data yet. Make sure the poller is running.</div>
          )}
        </div>

        {stats && (
          <div className="frequency-stats" style={{
            marginTop: 'auto', padding: '12px', borderTop: `1px solid ${PANEL_BORDER}`,
            fontSize: 11, color: '#f0f4f3', opacity: 0.85,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6, opacity: 1 }}>Table</div>
            <div>Rows: <strong>{stats.row_count.toLocaleString()}</strong></div>
            <div>Size: <strong>{stats.total_size}</strong></div>
            <div style={{ marginTop: 4, opacity: 0.7 }}>
              {formatTimestamp(stats.first_ts)} →<br/>{formatTimestamp(stats.last_ts)}
            </div>
          </div>
        )}
      </div>

      {/* ===== Main content ===== */}
      <div className="climate-main">
        {selectedSite === 'battery' ? (
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, minHeight: 0 }}>
            <BatterySimulationView />
          </div>
        ) : (
        <>
        <div className="climate-toolbar">
          <div className="climate-toolbar-tabs">
            {RANGES.map(r => (
              <button
                key={r.key}
                className={`toolbar-tab ${range === r.key ? 'active' : ''}`}
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
          {data && (
            <div style={{ marginLeft: 'auto', fontSize: 11, color: PANEL_TEXT_DIM }}>
              {data.point_count.toLocaleString()} points · bucket {data.bucket}
            </div>
          )}
        </div>

        <div className="climate-data-area">
          {!selectedSite && (
            <div className="location-empty">Select a site to view frequency data.</div>
          )}
          {loading && !data && <CenecLoadingBlock message="Loading frequency data..." />}
          {selectedSite && data && (
            <>
              <Panel
                title={`${data.label} — Frequency`}
                onExpand={() => openChartFullscreen(freqTraces, freqLayout, `${data.label} — Frequency`)}
              >
                <Plot
                  data={freqTraces}
                  layout={freqLayout}
                  config={plotConfig}
                  style={{ width: '100%', height: 620 }}
                />
                {calcs && (
                  <div style={{
                    display: 'flex', gap: 18, padding: '6px 14px 10px',
                    borderTop: `1px solid ${PANEL_BORDER}`,
                    fontSize: 11, fontFamily: 'Inter, "Work Sans", sans-serif',
                  }}>
                    {[
                      ['Min',      calcs.min.toFixed(3) + ' Hz'],
                      ['Max',      calcs.max.toFixed(3) + ' Hz'],
                      ['Variance', calcs.variance.toExponential(2)],
                      ['Last',     calcs.last.toFixed(3) + ' Hz'],
                    ].map(([label, val]) => (
                      <div key={label} style={{ display: 'flex', gap: 6 }}>
                        <span style={{ color: PANEL_TEXT_DIM }}>{label}</span>
                        <span style={{ color: PANEL_TEXT, fontWeight: 500 }}>{val}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              {gaugeTrace.length > 0 && (
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                  <div style={{ width: '100%', maxWidth: 520 }}>
                    <Panel
                      title={`${data.label} — Last reading`}
                      onExpand={() => openChartFullscreen(gaugeTrace, gaugeLayout, `${data.label} — Last reading`)}
                    >
                      <Plot
                        data={gaugeTrace}
                        layout={{ ...gaugeLayout, height: 280 }}
                        config={plotConfig}
                        style={{ width: '100%', height: 280 }}
                        useResizeHandler
                      />
                    </Panel>
                  </div>
                </div>
              )}

              {selectedSiteRecord && (
                <div style={{
                  padding: '8px 14px', marginTop: 12, fontSize: 11,
                  color: PANEL_TEXT_DIM, fontFamily: 'Inter, "Work Sans", sans-serif',
                }}>
                  Latest sample: <span style={{ color: PANEL_TEXT }}>
                    {selectedSiteRecord.last_value !== null
                      ? `${selectedSiteRecord.last_value.toFixed(4)} Hz`
                      : '—'}
                  </span>
                  {' '}at {formatTimestamp(selectedSiteRecord.last_ts)}
                </div>
              )}
            </>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default FrequencyDataView;
